import { useMemo } from 'react';
import { Transition } from '@headlessui/react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import cx from 'classnames';

import Icon from 'components/Icon';
import { useApp } from 'lib/contexts/save-in-app-context';
import { TAISearch } from 'lib/models/ai-search';
import { PAGE_ROUTES } from 'lib/page-routes';

import styles from './AISearchSuggestions.module.scss';

type AISearchSuggestionsProps = {
  className?: string;
  isOpen: boolean;
  searchText: string;
  searchBarRef: React.MutableRefObject<boolean>;
};

const AISearchSuggestions: React.FC<AISearchSuggestionsProps> = ({ className, isOpen, searchText, searchBarRef }) => {
  const router = useRouter();
  const [t] = useTranslation('search.results');
  const { aiSearches } = useApp();

  const searchMatches = useMemo(() => {
    return aiSearches.filter((aiSearch) => aiSearch.name.toLowerCase().includes(searchText.toLowerCase()));
  }, [aiSearches, searchText]);

  const handleAIChatPageChange = (aiSearch: TAISearch) => {
    router.push(PAGE_ROUTES.AI_SEARCH_RESULTS.replace('[searchId]', aiSearch.id));
  };

  const handleNewAISearch = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    event.stopPropagation();
    event.preventDefault();
    const route = `${PAGE_ROUTES.AI_SEARCH}?q=${searchText}`;
    router.push(route);
  };

  const handleMouseEnter = () => {
    searchBarRef.current = true;
  };

  const handleMouseLeave = () => {
    searchBarRef.current = false;
  };

  return (
    <Transition
      show={isOpen}
      enter={styles.enter}
      enterFrom={styles['enter-from']}
      enterTo={styles['enter-to']}
      leave={styles.leave}
      leaveFrom={styles['leave-from']}
      leaveTo={styles['leave-to']}
      as="section"
      className={cx(className, styles.container)}
    >
      <ul onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        <li className={cx(styles['search-row'], styles['search-with-ai'])} onClick={handleNewAISearch}>
          <Icon iconName="icon_magic-wand" />
          <span>{t('search-bar.ai-suggestions.search-with-ai')}</span>
        </li>
        {searchMatches.map((aiSearch) => (
          <li key={aiSearch.id} className={styles['search-row']} onClick={() => handleAIChatPageChange(aiSearch)}>
            <p className={styles['search-name']}>{aiSearch.name}</p>
          </li>
        ))}
      </ul>
    </Transition>
  );
};

export default AISearchSuggestions;
